/* Find a stockist (Blue Fish Imports custom sections). One remembered place for the whole site:
   the location bar sets it, and every product and range page shows its nearest stockists from it.
   Shops sort by distance; nearby ones (within nearKm) come green first (delivered in the last 3
   months), then yellow (3 to 6), then red (6 to 12). The place list (ABS postal areas and suburbs,
   CC BY 4.0) loads on first search. */
(() => {
  if (window.StockistFinder) return;
  const STORE_KEY = 'stockist-finder:place';
  const PAGE = 10;
  const NEARBY = 3;
  let placesPromise = null;

  const loadPlaces = (url) => (placesPromise ||= fetch(url).then((r) => r.json()));
  const fold = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const km = (a, b) => {
    const rad = Math.PI / 180, dLat = (b[0] - a[0]) * rad, dLng = (b[1] - a[1]) * rad;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * rad) * Math.cos(b[0] * rad) * Math.sin(dLng / 2) ** 2;
    return 12742 * Math.asin(Math.sqrt(h));
  };
  const distance = (d) => `${d < 10 ? d.toFixed(1) : Math.round(d)} km`;
  const el = (tag, cls, text) => {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  };
  const dot = (level) => el('span', `stockist-finder__dot stockist-finder__dot--${level}`);
  const directionsUrl = (s) => 'https://www.google.com/maps/dir/?api=1&destination=' +
    encodeURIComponent(`${s.name}, ${s.street}, ${s.suburb} ${s.state} ${s.postcode}`);
  // Line icons in the theme's style: phone and store are Focal's own pictos; directions is a
  // navigation arrow drawn to match.
  const ICONS = {
    directions: '<path d="M3.5 10.5L20.5 3.5L13.5 20.5L11.5 12.5L3.5 10.5Z" stroke="currentColor" stroke-linejoin="round"></path>',
    phone: '<path fill-rule="evenodd" clip-rule="evenodd" d="M20.6636 16.7325L17.6844 13.7366C17.2337 13.2827 16.4999 13.2827 16.048 13.7343L13.4005 16.3802L7.62246 10.6056L10.2734 7.95613C10.7241 7.5057 10.7253 6.77463 10.2746 6.32305L7.29311 3.33869C6.84126 2.8871 6.10976 2.8871 5.65791 3.33869L3.00462 5.98927L3 5.9858C3 14.2783 9.72568 21 18.023 21L20.6613 18.3633C21.1119 17.9129 21.1131 17.1841 20.6636 16.7325Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>',
    store: '<path d="M21.125 6L20 3H4L2.875 6M21.125 6L21.5 7C21.5 7 21.5 8.75 20.5 9.625C20.3366 9.76798 20.1692 9.88453 20 9.97831M21.125 6H2.875M2.875 6L2.5 7C2.5 7 2.5 8.75 3.5 9.625C3.66341 9.76798 3.83078 9.88453 4 9.97831M4 9.97831C5.66138 10.899 7.5 9.625 7.5 9.625C7.5 9.625 8.5 10.5 9.5 10.5C10.5 10.5 12 9.625 12 9.625C12 9.625 13.5 10.5 14.5 10.5C15.5 10.5 16.5 9.625 16.5 9.625C16.5 9.625 18.3386 10.899 20 9.97831M4 9.97831V21H10M20 9.97831V21H14M10 21V14H14V21M10 21H14" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>',
  };
  const icon = (name, stroke) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '18');
    svg.setAttribute('height', '18');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke-width', stroke || 1.5);
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML = ICONS[name];
    return svg;
  };
  const chevron = () => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 12 8');
    svg.setAttribute('width', '12');
    svg.setAttribute('height', '8');
    svg.setAttribute('aria-hidden', 'true');
    svg.classList.add('stockist-nearby__chevron');
    svg.innerHTML = '<path fill="none" d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="1.5"></path>';
    return svg;
  };

  // Only a touch screen (a phone) can call: elsewhere a tel: link opens FaceTime, Teams or an
  // "open with" prompt, so the number is plain text there.
  const canCall = () => matchMedia('(pointer: coarse)').matches;
  const telHref = (phone) => `tel:${phone.replace(/[^\d+]/g, '')}`;

  // What visitors do with the finder, as events in the site's Google Analytics where it has one:
  // the shop's name, and how a place was set (never where the visitor is). Where GA4 sits in Tag
  // Manager there may be no gtag() and events not addressed to it are dropped, so each is queued
  // as gtag would queue it, to the page's GA4 IDs.
  function gtag() { window.dataLayer.push(arguments); }
  const track = (name, params) => {
    const ids = Object.keys(window.google_tag_manager || {}).filter((k) => k.startsWith('G-'));
    if (window.dataLayer && ids.length) gtag('event', name, { ...params, send_to: ids });
  };
  document.addEventListener('click', (e) => {
    const a = e.target.closest?.('a[data-stockist-event]');
    if (a) track(a.dataset.stockistEvent, { shop: a.dataset.stockistShop });
  });

  // A shop's buttons: Directions, its phone number (a button only where it can call), and its
  // full range.
  const shopButtons = (s, { labels, iconStroke }) => {
    const action = (label, href, glyph, event, external) => {
      const a = el('a', 'stockist-nearby__button text--small');
      a.append(icon(glyph, iconStroke), el('span', null, label));
      a.href = href;
      a.dataset.stockistEvent = event;
      a.dataset.stockistShop = s.name;
      if (external) {
        a.target = '_blank';
        a.rel = 'noopener';
      }
      return a;
    };
    const buttons = el('div', 'stockist-nearby__buttons');
    buttons.append(action(labels.directions, directionsUrl(s), 'directions', 'stockist_directions', true));
    if (s.phone && canCall()) buttons.append(action(s.phone, telHref(s.phone), 'phone', 'stockist_call'));
    else if (s.phone) {
      const number = el('span', 'stockist-nearby__button stockist-nearby__button--text text--small');
      number.append(icon('phone', iconStroke), el('span', null, s.phone));
      buttons.append(number);
    }
    if (s.url) buttons.append(action(labels.shopPage, s.url, 'store', 'stockist_shop_page'));
    return buttons;
  };
  const addressOf = (s) => `${s.street}, ${s.suburb} ${s.state} ${s.postcode}`;
  const shopKey = (s) => `${s.name}|${s.street}|${s.postcode}`;

  const brandOf = (s, brands) => brands.find((b) => (b.chain && s.chain === b.chain) || (b.name && s.name.startsWith(b.name))
    || (b.shops && b.shops.includes(s.url?.split('/').pop())));

  // A shop's logo where we have one (the chains, Hot Tackle, Freddy's and independents' own), else a
  // store icon.
  const brandMark = (s, iconStroke) => {
    if (!s.brand) {
      const mark = el('span', 'stockist-brand stockist-brand--none');
      mark.append(icon('store', iconStroke));
      return mark;
    }
    const img = el('img', 'stockist-brand');
    img.src = s.brand.logo;
    img.alt = s.brand.label;
    img.width = 44;
    img.height = 22;
    return img;
  };

  // A list of shops where each row opens its actions (Directions, call, full range) underneath.
  // With `levels`, rows carry the product's confidence dot and label; `address` adds the street;
  // `brands` leads each row with the shop's logo (`brandMark`); `onOpen` hears which shop was opened.
  let listCount = 0;
  const shopList = (shops, { labels, iconStroke, levels, address, brands, onOpen, onClose, distances = true }) => {
    const id = `stockist-list-${++listCount}`;
    const rows = el('ul', 'stockist-nearby__list');
    shops.forEach((s, i) => {
      const li = el('li', 'stockist-nearby__item');
      const row = el('button', 'stockist-nearby__row text--small');
      row.type = 'button';
      row.setAttribute('aria-expanded', 'false');
      row.setAttribute('aria-controls', `${id}-${i}`);
      const name = el('span', 'stockist-nearby__name');
      name.append(el('span', null, s.name));
      if (address) name.append(el('span', 'stockist-nearby__suburb text--subdued', `${s.suburb} ${s.state}`));
      if (levels) row.append(dot(s.level));
      if (brands) row.append(brandMark(s, iconStroke));
      row.append(name);
      if (distances && s.km != null) row.append(el('span', 'stockist-nearby__distance text--subdued', distance(s.km)));
      row.append(chevron());
      const actions = el('div', 'stockist-nearby__actions');
      actions.id = `${id}-${i}`;
      actions.hidden = true;
      if (levels) actions.append(el('p', 'stockist-nearby__level text--xsmall text--subdued', labels.levels[s.level]));
      if (address) actions.append(el('p', 'stockist-nearby__level text--xsmall text--subdued', addressOf(s)));
      actions.append(shopButtons(s, { labels, iconStroke }));
      row.addEventListener('click', () => {
        const open = row.getAttribute('aria-expanded') !== 'true';
        const top = row.getBoundingClientRect().top;
        rows.querySelectorAll('.stockist-nearby__row').forEach((r) => r.setAttribute('aria-expanded', 'false'));
        rows.querySelectorAll('.stockist-nearby__actions').forEach((x) => { x.hidden = true; });
        row.setAttribute('aria-expanded', open ? 'true' : 'false');
        actions.hidden = !open;
        // The browser's scroll anchoring holds what's below the list still, so a row opening near
        // the bottom of the screen pushes the page up and the panel seems to open upwards: keep
        // the tapped row where it was, so the panel opens downwards.
        const hold = () => {
          const moved = row.getBoundingClientRect().top - top;
          if (Math.abs(moved) > 1) window.scrollBy(0, moved);
        };
        hold();
        requestAnimationFrame(hold);
        if (open) track('stockist_shop_open', { shop: s.name });
        if (open && onOpen) onOpen(s);
        if (!open && onClose) onClose(s);
      });
      li.append(row, actions);
      rows.append(li);
    });
    return rows;
  };

  // The one place, shared by every part of the page and remembered across the site.
  const Place = {
    get() {
      try { return JSON.parse(localStorage.getItem(STORE_KEY) || 'null'); } catch (e) { return null; }
    },
    set(place) {
      localStorage.setItem(STORE_KEY, JSON.stringify(place));
      document.dispatchEvent(new CustomEvent('stockist:place', { detail: place }));
    },
    on(fn) {
      document.addEventListener('stockist:place', (e) => fn(e.detail));
    },
  };

  // A shop's name matches when every word typed starts one of its words ("bcf lab", "tackle w").
  const words = (s) => fold(s).split(/[^a-z0-9]+/).filter(Boolean);
  const nameMatches = (name, typed) => {
    const have = words(name);
    return typed.every((t) => have.some((w) => w.startsWith(t)));
  };

  // A postcode or suburb field with suggestions, and a "use my location" button. With `shops`
  // (the Stockists page), shops whose name matches follow the places; picking one calls `onShop`.
  class PlaceSearch {
    constructor(form, { placesUrl, labels, say, shops, onShop, iconStroke }) {
      this.labels = labels;
      this.placesUrl = placesUrl;
      this.say = say;
      this.shops = shops;
      this.onShop = onShop;
      this.iconStroke = iconStroke;
      this.input = form.querySelector('input[name="q"]');
      this.suggest = form.querySelector('[role="listbox"]');
      this.options = [];
      this.active = -1;
      this.input.addEventListener('focus', () => loadPlaces(this.placesUrl), { once: true });
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      form.addEventListener('submit', (e) => { e.preventDefault(); this.pick(this.active); });
      form.querySelector('[data-stockist-locate]').addEventListener('click', () => this.locate());
      this.suggest.addEventListener('mousedown', (e) => e.preventDefault());
      this.suggest.addEventListener('click', (e) => {
        const li = e.target.closest('[role="option"]');
        if (li) this.pick(Number(li.dataset.index));
      });
      this.show(Place.get());
      Place.on((place) => this.show(place));
    }

    show(place) {
      if (!place) return;
      this.input.value = place.current ? this.labels.currentLocation : place.label;
      this.input.classList.add('is-filled');
    }

    async onInput() {
      const q = fold(this.input.value.trim());
      if (q.length < 2) return this.showOptions([]);
      const places = await loadPlaces(this.placesUrl);
      let options = [];
      const label = ([name, state, , , postcode]) => `${name} ${state}${postcode ? ` ${postcode}` : ''}`;
      if (/^\d{3,4}$/.test(q)) {
        // a postcode: its suburbs, then the postcode itself where it names none
        options = places.suburbs.filter((s) => s[4] && s[4].startsWith(q)).slice(0, 6)
          .map((s) => ({ label: label(s), at: [s[2], s[3]] }));
        if (!options.length) {
          options = Object.keys(places.postcodes).filter((p) => p.startsWith(q)).slice(0, 6)
            .map((p) => ({ label: p, at: places.postcodes[p] }));
        }
      } else {
        const starts = [], contains = [];
        for (const s of places.suburbs) {
          const f = fold(s[0]);
          if (f.startsWith(q)) starts.push({ label: label(s), at: [s[2], s[3]] });
          else if (f.includes(` ${q}`)) contains.push({ label: label(s), at: [s[2], s[3]] });
          if (starts.length >= 6) break;
        }
        options = starts.concat(contains).slice(0, 6);
        if (this.shops) {
          // Shops by name, nearest the visitor's place first where there is one, else A to Z.
          const typed = words(q);
          const from = Place.get()?.at;
          const shops = this.shops.filter((s) => nameMatches(s.name, typed))
            .sort((a, b) => (from ? km(from, a.at) - km(from, b.at) : a.name.localeCompare(b.name)))
            .slice(0, 5).map((s) => ({ label: s.name, shop: s }));
          options = options.slice(0, shops.length ? 3 : 6).concat(shops);
        }
      }
      this.showOptions(options);
    }

    showOptions(options) {
      this.options = options;
      this.active = options.length ? 0 : -1;
      this.suggest.replaceChildren(...options.map((o, i) => {
        const li = el('li', 'text--small', o.shop ? null : o.label);
        if (o.shop) {
          li.classList.add('stockist-finder__shop');
          const name = el('span', 'stockist-nearby__name');
          name.append(el('span', null, o.shop.name), el('span', 'stockist-nearby__suburb text--subdued', `${o.shop.suburb} ${o.shop.state}`));
          li.append(brandMark(o.shop, this.iconStroke), name);
        }
        li.id = `${this.suggest.id}-${i}`;
        li.setAttribute('role', 'option');
        li.dataset.index = i;
        li.setAttribute('aria-selected', i === this.active ? 'true' : 'false');
        return li;
      }));
      this.suggest.hidden = !options.length;
      this.input.setAttribute('aria-expanded', options.length ? 'true' : 'false');
    }

    onKey(e) {
      if (!this.options.length || !['ArrowDown', 'ArrowUp', 'Escape'].includes(e.key)) return;
      e.preventDefault();
      if (e.key === 'Escape') return this.showOptions([]);
      this.active = (this.active + (e.key === 'ArrowDown' ? 1 : -1) + this.options.length) % this.options.length;
      [...this.suggest.children].forEach((li, i) => li.setAttribute('aria-selected', i === this.active ? 'true' : 'false'));
      this.input.setAttribute('aria-activedescendant', `${this.suggest.id}-${this.active}`);
    }

    async pick(index) {
      if (index < 0) {
        await this.onInput();
        if (!this.options.length) return this.say(this.labels.noPlace);
        index = 0;
      }
      const place = this.options[index];
      this.showOptions([]);
      if (place.shop) {
        track('stockist_shop_search', { shop: place.shop.name });
        return this.onShop(place.shop);
      }
      track('stockist_place', { method: 'search', place: place.label });
      Place.set(place);
    }

    locate() {
      if (!navigator.geolocation || !window.isSecureContext) return this.say(this.labels.noLocation);
      this.say(this.labels.locating);
      // The browser's own timeout doesn't run while its permission prompt is open or the phone's
      // location is off, so we keep our own: an answer after this is ignored.
      let done = false;
      const finish = (fn) => (...args) => { if (!done) { done = true; clearTimeout(timer); fn(...args); } };
      const timer = setTimeout(finish(() => this.say(this.labels.locationOff)), 15000);
      navigator.geolocation.getCurrentPosition(
        finish(async (pos) => {
          const at = [pos.coords.latitude, pos.coords.longitude];
          // Name the spot by its nearest suburb, so the page says where it thinks you are.
          let label = this.labels.yourLocation;
          try {
            const places = await loadPlaces(this.placesUrl);
            let best = null;
            for (const s of places.suburbs) {
              const d = km(at, [s[2], s[3]]);
              if (!best || d < best.d) best = { d, s };
            }
            if (best && best.d < 25) label = `${best.s[0]} ${best.s[1]}${best.s[4] ? ` ${best.s[4]}` : ''}`;
          } catch (e) { /* keep the plain label */ }
          this.say('');
          track('stockist_place', { method: 'location' });
          Place.set({ label, at, current: true });
        }),
        finish((err) => this.say(err.code === 1 ? this.labels.locationBlocked : this.labels.locationOff)),
        { enableHighAccuracy: false, timeout: 12000, maximumAge: 600000 });
    }
  }

  // The page's stockists (product or range): the drawer list and the nearby panels.
  class Finder {
    constructor(drawer) {
      this.drawer = drawer;
      const data = JSON.parse(drawer.querySelector('[data-stockist-data]').textContent);
      this.labels = data.labels;
      this.nearKm = data.nearKm;
      this.iconStroke = data.iconStroke;
      this.brands = data.brands || [];
      this.shops = data.shops.map(([name, street, suburb, state, postcode, phone, lat, lng, level, url, chain]) =>
        ({ name, street, suburb, state, postcode, phone, at: [lat, lng], level, url, chain }));
      this.shops.forEach((s) => { s.brand = brandOf(s, this.brands); });
      this.placesUrl = data.places;
      this.bars = [];
      this.status = drawer.querySelector('[data-stockist-status]');
      this.list = drawer.querySelector('[data-stockist-list]');
      this.more = drawer.querySelector('[data-stockist-more]');
      this.more.addEventListener('click', () => this.render(this.shown + PAGE));
      this.search = new PlaceSearch(drawer.querySelector('form'), {
        placesUrl: data.places, labels: this.labels, say: (t) => this.say(t),
      });
      this.panels = [];
      const place = Place.get();
      if (place) this.setPlace(place);
      Place.on((p) => this.setPlace(p));
    }

    open() {
      this.drawer.open = true;
    }

    ranked(place) {
      const withDistance = this.shops.map((s) => ({ ...s, km: km(place.at, s.at) }));
      // Nearby: green first, then yellow, then red, each nearest first; beyond that, nearest first.
      const near = withDistance.filter((s) => s.km <= this.nearKm).sort((a, b) => b.level - a.level || a.km - b.km);
      const far = withDistance.filter((s) => s.km > this.nearKm).sort((a, b) => a.km - b.km);
      return { sorted: near.concat(far), near: near.length };
    }

    // A shop as a card: its logo, name, suburb and distance, how often it has the product, and
    // the same buttons as a list row.
    card(s) {
      const card = el('article', 'stockist-card');
      const head = el('div', 'stockist-card__head');
      const text = el('div');
      const name = el('p', 'stockist-card__name text--small');
      name.append(this.nameOf(s));
      text.append(name, el('p', 'stockist-card__where text--xsmall text--subdued', `${s.suburb} ${s.state} · ${distance(s.km)}`));
      head.append(brandMark(s, this.iconStroke), text);
      const level = el('p', 'stockist-card__level text--xsmall');
      level.append(dot(s.level), this.labels.levels[s.level]);
      card.append(head, level, shopButtons(s, { labels: this.labels, iconStroke: this.iconStroke }));
      return card;
    }

    setPlace(place) {
      this.place = place;
      const { sorted, near } = this.ranked(place);
      this.sorted = sorted;
      this.heading = near
        ? this.labels.near.replace('[place]', place.label)
        : this.labels.nearest.replace('[km]', this.nearKm);
      this.render(near ? Math.max(Math.min(near, PAGE), 3) : 5);
      this.panels.forEach((panel) => this.renderPanel(panel));
      this.bars?.forEach((show) => show());
    }

    say(text) {
      this.status.textContent = text;
    }

    // The rows every list of this page's shops uses: logo, confidence dot, name, suburb, distance;
    // tap for the label, the address and the buttons.
    rows(shops) {
      return shopList(shops, { labels: this.labels, iconStroke: this.iconStroke, levels: true, brands: true, address: true });
    }

    render(count) {
      this.shown = Math.min(count, this.sorted.length);
      this.say(this.heading);
      this.list.replaceChildren(this.rows(this.sorted.slice(0, this.shown)));
      this.more.hidden = this.shown >= this.sorted.length;
    }

    nameOf(s) {
      if (!s.url) return document.createTextNode(s.name);
      const link = el('a', 'link--faded', s.name);
      link.href = s.url;
      return link;
    }

    // A range page's bar: a postcode search until there's a place, then the place, a Change link
    // that brings the search back, and the nearest shops as cards (the panel inside the bar).
    addBar(bar) {
      const form = bar.querySelector('[data-stockist-bar-search]');
      const status = bar.querySelector('[data-stockist-bar-status]');
      new PlaceSearch(form, { placesUrl: this.placesUrl, labels: this.labels, say: (t) => { status.textContent = t; } });
      const change = bar.querySelector('[data-stockist-bar-change]');
      // With a place, Change opens the search and then reads Cancel to close it again (as does Escape).
      const editing = (open) => {
        form.hidden = !open;
        change.textContent = open ? this.labels.cancel : this.labels.change;
        change.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) form.querySelector('input').focus();
      };
      const show = () => {
        const place = this.place;
        bar.querySelector('[data-stockist-bar-place]').hidden = !place;
        if (place) bar.querySelector('[data-stockist-bar-place-text]').textContent = this.labels.nearbyHeading.replace('[place]', place.label);
        editing(false);
        form.hidden = !!place;
        status.textContent = '';
      };
      change.addEventListener('click', () => editing(form.hidden));
      form.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.place && form.querySelector('[role="listbox"]').hidden) editing(false);
      });
      this.bars.push(show);
      show();
    }

    // A panel on the page itself: the nearest few shops, or a prompt to set a place.
    addPanel(panel) {
      this.panels.push(panel);
      panel.addEventListener('click', (e) => {
        if (e.target.closest('[data-stockist-all]')) {
          e.preventDefault();
          this.open();
        }
      });
      this.renderPanel(panel);
    }

    renderPanel(panel) {
      const place = this.place;
      if (panel.dataset.stockistLayout === 'cards') {
        // Cards side by side on wide screens; on narrower ones the same shops as tappable rows.
        if (!place) return panel.replaceChildren();
        const nearest = this.sorted.slice(0, NEARBY);
        const cards = el('div', 'stockist-cards__grid');
        cards.append(...nearest.map((s) => this.card(s)));
        const rows = this.rows(nearest);
        rows.classList.add('stockist-cards__rows');
        panel.replaceChildren(cards, rows);
        return;
      }
      if (!place) {
        const prompt = el('button', 'stockist-nearby__prompt link text--small', this.labels.setLocation);
        prompt.type = 'button';
        prompt.dataset.stockistAll = '';
        panel.replaceChildren(prompt);
        return;
      }
      const head = el('p', 'stockist-nearby__head text--small');
      head.append(el('span', 'stockist-nearby__place', this.labels.nearbyHeading.replace('[place]', place.label)));
      const change = el('button', 'link text--subdued', this.labels.change);
      change.type = 'button';
      change.dataset.stockistAll = '';
      head.append(change);
      const rows = this.rows(this.sorted.slice(0, NEARBY));
      const all = el('button', 'link text--small', this.labels.seeAll.replace('[count]', this.sorted.length));
      all.type = 'button';
      all.dataset.stockistAll = '';
      panel.replaceChildren(head, rows, all);
    }
  }

  // The site-wide location bar and its drawer (no shops of its own).
  class LocationBar {
    constructor(root) {
      const data = JSON.parse(root.querySelector('[data-stockist-location-data]').textContent);
      this.labels = data.labels;
      this.text = root.querySelector('[data-stockist-location-text]');
      this.change = root.querySelector('.stockist-location__change');
      this.drawer = document.getElementById(root.dataset.drawer);
      this.status = this.drawer.querySelector('[data-stockist-status]');
      this.search = new PlaceSearch(this.drawer.querySelector('form'), {
        placesUrl: data.places, labels: this.labels, say: (t) => { this.status.textContent = t; },
      });
      root.querySelector('[data-stockist-location-open]').addEventListener('click', () => { this.drawer.open = true; });
      this.show(Place.get());
      Place.on((place) => {
        this.show(place);
        this.status.textContent = '';
        this.drawer.open = false;
      });
    }

    show(place) {
      this.text.textContent = place ? this.labels.near.replace('[place]', place.label) : this.labels.unset;
      this.change.hidden = !place;
    }
  }

  // The Stockists page's map: MapLibre with OpenFreeMap tiles, loaded the first time the map is on
  // screen (straight away beside the list; on phones, when the visitor switches to it). Shows every
  // shop the filters allow and frames the ones the list is about.
  let mapLibrary = null;
  const loadMapLibrary = ({ js, css }) => (mapLibrary ||= new Promise((resolve, reject) => {
    const link = el('link');
    link.rel = 'stylesheet';
    link.href = css;
    const script = el('script');
    script.src = js;
    script.onload = () => resolve(window.maplibregl);
    script.onerror = reject;
    document.head.append(link, script);
  }));
  const AUSTRALIA = [[112.9, -43.7], [153.7, -10.6]];
  const lngLat = (at) => [at[1], at[0]];

  class ShopMap {
    constructor(box, { library, style, labels, iconStroke, brands, onMove }) {
      this.box = box;
      this.onMove = onMove;
      this.style = style;
      this.labels = labels;
      this.iconStroke = iconStroke;
      this.library = library;
      this.brands = brands;
      // A mouse opens a shop's card on hover; touch opens it on tap, with a close button.
      this.hover = matchMedia('(hover: hover) and (pointer: fine)').matches;
      const watch = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        watch.disconnect();
        this.load();
      });
      watch.observe(box);
    }

    async load() {
      const gl = this.gl = await loadMapLibrary(this.library);
      const brand = getComputedStyle(document.documentElement).getPropertyValue('--primary-button-background').trim();
      const colour = brand ? `rgb(${brand})` : '#1c1c1c';
      const map = this.map = new gl.Map({
        container: this.box, style: this.style, bounds: AUSTRALIA, fitBoundsOptions: { padding: 24 },
        dragRotate: false, pitchWithRotate: false, touchPitch: false,
      });
      map.touchZoomRotate.disableRotation();
      map.keyboard.disableRotation();
      map.addControl(new gl.NavigationControl({ showCompass: false }), 'top-right');
      map.on('load', async () => {
        await this.addPins(colour);
        map.addSource('shops', { type: 'geojson', data: this.features([]), cluster: true, clusterRadius: 36, clusterMaxZoom: 11 });
        map.addSource('place', { type: 'geojson', data: this.features([]) });
        map.addLayer({ id: 'place-halo', type: 'circle', source: 'place', paint: { 'circle-radius': 18, 'circle-color': '#2f7de1', 'circle-opacity': 0.15 } });
        map.addLayer({ id: 'place', type: 'circle', source: 'place', paint: { 'circle-radius': 7, 'circle-color': '#2f7de1', 'circle-stroke-width': 3, 'circle-stroke-color': '#fff' } });
        map.addLayer({ id: 'clusters', type: 'circle', source: 'shops', filter: ['has', 'point_count'],
          paint: { 'circle-color': colour, 'circle-radius': ['step', ['get', 'point_count'], 15, 10, 19, 50, 24], 'circle-stroke-width': 2, 'circle-stroke-color': '#fff' } });
        map.addLayer({ id: 'cluster-count', type: 'symbol', source: 'shops', filter: ['has', 'point_count'],
          layout: { 'text-field': ['get', 'point_count_abbreviated'], 'text-font': ['Noto Sans Bold'], 'text-size': 12, 'text-allow-overlap': true },
          paint: { 'text-color': '#fff' } });
        map.addLayer({ id: 'shop-selected', type: 'circle', source: 'shops', filter: ['==', ['get', 'i'], -1],
          paint: { 'circle-radius': ['case', ['==', ['get', 'b'], 'stockist-independent'], 19, 22], 'circle-color': colour, 'circle-opacity': 0.25 } });
        map.addLayer({ id: 'shops', type: 'symbol', source: 'shops', filter: ['!', ['has', 'point_count']],
          layout: { 'icon-image': ['get', 'b'], 'icon-allow-overlap': true, 'icon-ignore-placement': true } });
        map.on('click', 'clusters', async (e) => {
          const cluster = e.features[0];
          const zoom = await map.getSource('shops').getClusterExpansionZoom(cluster.properties.cluster_id);
          map.easeTo({ center: cluster.geometry.coordinates, zoom });
        });
        ['shops'].forEach((layer) => {
          map.on('click', layer, (e) => this.open(this.view.shops[e.features[0].properties.i]));
          if (this.hover) {
            map.on('mouseenter', layer, (e) => this.open(this.view.shops[e.features[0].properties.i]));
            map.on('mouseleave', layer, () => this.closeSoon());
          }
        });
        ['clusters', 'shops'].forEach((layer) => {
          map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer'; });
          map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = ''; });
        });
        // The list follows what's on screen, except when it moved the map itself (a row opened).
        map.on('moveend', (e) => { if (!e.fromList) this.onMove?.(); });
        this.ready = true;
        if (this.view) this.show(this.view);
      });
    }

    // Pins: the logo of a brand with a `pin`, or a store icon in the theme's colour, on a white disc; drawn once at
    // the screen's pixel ratio.
    async addPins(colour) {
      const ratio = Math.min(window.devicePixelRatio || 1, 3);
      const store = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48" fill="none" stroke-width="1.5">${ICONS.store.replaceAll('currentColor', colour)}</svg>`;
      const pins = this.brands.filter((b) => b.pin).concat({ key: 'stockist-independent', pin: `data:image/svg+xml,${encodeURIComponent(store)}`, pinScale: 0.52, size: 30 });
      await Promise.all(pins.map((brand) => new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // app assets come from cdn.shopify.com; without this the canvas can't be read
        img.onload = () => {
          const px = (brand.size || 34) * ratio;
          const canvas = document.createElement('canvas');
          canvas.width = canvas.height = px;
          const ctx = canvas.getContext('2d');
          ctx.shadowColor = 'rgba(0, 0, 0, .3)';
          ctx.shadowBlur = 3 * ratio;
          ctx.shadowOffsetY = ratio;
          ctx.beginPath();
          ctx.arc(px / 2, px / 2, px / 2 - 3 * ratio, 0, Math.PI * 2);
          ctx.fillStyle = '#fff';
          ctx.fill();
          ctx.shadowColor = 'transparent';
          const box = px * brand.pinScale;
          const scale = Math.min(box / img.naturalWidth, box / img.naturalHeight);
          const w = img.naturalWidth * scale, h = img.naturalHeight * scale;
          ctx.drawImage(img, (px - w) / 2, (px - h) / 2, w, h);
          this.map.addImage(brand.key, ctx.getImageData(0, 0, px, px), { pixelRatio: ratio });
          resolve();
        };
        img.onerror = resolve;
        img.src = brand.pin;
      })));
    }

    features(points) {
      return { type: 'FeatureCollection', features: points.map(([at, properties]) => ({ type: 'Feature', properties, geometry: { type: 'Point', coordinates: lngLat(at) } })) };
    }

    // view: { shops (every pin), focus (the shops to frame), place }
    show(view) {
      this.view = view;
      if (!this.ready) return;
      this.popup?.remove();
      this.map.getSource('shops').setData(this.features(view.shops.map((s, i) => [s.at, { i, b: (s.brand?.pin && s.brand.key) || 'stockist-independent' }])));
      this.map.getSource('place').setData(this.features(view.place ? [[view.place.at, {}]] : []));
      this.select(null);
      this.frame();
    }

    frame() {
      if (!this.ready || !this.box.offsetParent) return;
      const points = this.view.focus.map((s) => s.at).concat(this.view.place ? [this.view.place.at] : []);
      if (!points.length) return this.map.fitBounds(AUSTRALIA, { padding: 24 });
      const bounds = new this.gl.LngLatBounds(lngLat(points[0]), lngLat(points[0]));
      points.forEach((at) => bounds.extend(lngLat(at)));
      this.map.fitBounds(bounds, { padding: 56, maxZoom: 13, duration: 600 });
    }

    // The map has just come on screen (phones switch to it): size it and frame the list again.
    reveal() {
      if (!this.ready) return;
      this.map.resize();
      this.frame();
    }

    highlight(s) {
      const i = s ? this.view.shops.indexOf(s) : -1;
      this.map.setFilter('shop-selected', ['all', ['!', ['has', 'point_count']], ['==', ['get', 'i'], i]]);
    }

    // A list row was opened: mark its pin and bring it into view. The list stays as it is, except
    // after a shop picked by name (`refill`): the map has moved to a new area, so the list follows it.
    select(s, refill = false) {
      if (!this.ready) return;
      this.highlight(s);
      if (s && this.box.offsetParent) this.map.easeTo({ center: lngLat(s.at), zoom: Math.max(this.map.getZoom(), 12), duration: 600 }, refill ? {} : { fromList: true });
    }

    // Whether the map is on screen and so decides what the list shows.
    drives() {
      return !!(this.ready && this.box.offsetParent);
    }

    visible(shops) {
      const bounds = this.map.getBounds();
      return shops.filter((s) => bounds.contains(lngLat(s.at)));
    }

    contains(at) {
      return this.map.getBounds().contains(lngLat(at));
    }

    centre() {
      const c = this.map.getCenter();
      return [c.lat, c.lng];
    }

    closeSoon() {
      clearTimeout(this.closing);
      this.closing = setTimeout(() => this.popup?.remove(), 300);
    }

    // A pin's card: the chain's logo, the shop, its address and the same buttons as its list row.
    open(s) {
      clearTimeout(this.closing);
      if (this.popup?.isOpen() && this.popupShop === s) return;
      const card = el('div', 'stockist-map__card');
      const brand = s.brand;
      const head = el('div', 'stockist-map__head');
      if (brand) {
        const logo = el('img', 'stockist-map__logo');
        logo.src = brand.logo;
        logo.alt = brand.label;
        head.append(logo);
      }
      const name = el('p', 'stockist-map__name text--small');
      if (s.url) {
        const link = el('a', 'link--faded', s.name);
        link.href = s.url;
        name.append(link);
      } else name.textContent = s.name;
      head.append(name);
      card.append(head, el('p', 'stockist-map__address text--xsmall text--subdued', addressOf(s)),
        shopButtons(s, { labels: this.labels, iconStroke: this.iconStroke }));
      if (!this.hover) {
        const close = el('button', 'stockist-map__close');
        close.type = 'button';
        close.setAttribute('aria-label', this.labels.close);
        close.innerHTML = '<svg viewBox="0 0 14 14" width="14" height="14" aria-hidden="true"><path d="M13 13L1 1M13 1L1 13" stroke="currentColor" stroke-width="1.5" fill="none"></path></svg>';
        close.addEventListener('click', () => this.popup?.remove());
        card.append(close);
      }
      this.popup?.remove();
      this.popupShop = s;
      // Touch: the card sits above its pin, and the map moves the pin below centre so the card fits.
      this.popup = new this.gl.Popup({ offset: brand ? 18 : 16, maxWidth: '320px', closeButton: false, focusAfterOpen: false,
        ...(this.hover ? {} : { anchor: 'bottom' }) })
        .setLngLat(lngLat(s.at)).setDOMContent(card).addTo(this.map);
      if (!this.hover) this.map.easeTo({ center: lngLat(s.at), offset: [0, Math.min(130, this.box.clientHeight / 4)], duration: 400 });
      this.popup.on('close', () => { if (this.popupShop === s) { this.popupShop = null; this.highlight(null); } });
      if (this.hover) {
        const box = this.popup.getElement();
        box.addEventListener('mouseenter', () => clearTimeout(this.closing));
        box.addEventListener('mouseleave', () => this.closeSoon());
      }
      this.highlight(s);
    }
  }

  // The Stockists page: every shop, nearest first once a place is set, else by state; a map beside
  // the list (on phones, a List / Map switch).
  class Directory {
    constructor(root) {
      const data = JSON.parse(root.querySelector('[data-stockist-directory-data]').textContent);
      this.labels = data.labels;
      this.iconStroke = data.iconStroke;
      // Logos for chains (by `chain`) and for a few important shops (by the start of the name).
      this.brands = data.brands || [];
      this.shops = data.shops.map(([name, chain, street, suburb, state, postcode, phone, lat, lng, url]) =>
        ({ name, chain, street, suburb, state, postcode, phone, at: [lat, lng], url }));
      this.shops.forEach((s) => { s.brand = brandOf(s, this.brands); });
      this.status = root.querySelector('[data-stockist-status]');
      this.list = root.querySelector('[data-stockist-list]');
      this.more = root.querySelector('[data-stockist-more]');
      this.results = root.querySelector('.stockist-directory__results');
      this.chains = root.querySelector('[data-stockist-chains]');
      this.states = root.querySelector('[data-stockist-states]');
      this.chain = '';
      this.state = data.states.find((st) => this.shops.some((s) => s.state === st)) || '';
      this.search = new PlaceSearch(root.querySelector('form'), {
        placesUrl: data.places, labels: this.labels, say: (t) => { this.status.textContent = t; },
        shops: this.shops, iconStroke: this.iconStroke, onShop: (s) => this.showShop(s),
      });
      this.chains.addEventListener('click', (e) => this.pick(e, 'chain', this.chains));
      this.states.addEventListener('click', (e) => this.pick(e, 'state', this.states));
      this.more.addEventListener('click', () => this.fill(this.shown + PAGE * 2));
      // Desktop: the list and map fill the rest of the screen below the page heading.
      const layout = root.querySelector('.stockist-directory__layout');
      const fit = () => root.style.setProperty('--stockist-top', `${Math.round(layout.getBoundingClientRect().top + window.scrollY)}px`);
      fit();
      window.addEventListener('resize', fit);
      const box = root.querySelector('[data-stockist-map]');
      if (box && 'IntersectionObserver' in window) {
        this.map = new ShopMap(box, { library: data.map, style: data.map.style, labels: this.labels, iconStroke: this.iconStroke,
          brands: this.brands, onMove: () => this.fill(PAGE * 2) });
      }
      root.querySelector('[data-stockist-views]')?.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-value]');
        if (!chip) return;
        root.dataset.view = chip.dataset.value;
        chip.parentElement.querySelectorAll('[data-value]').forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
        if (chip.dataset.value === 'map') this.map?.reveal();
        else this.fill(PAGE * 2);
      });
      this.place = Place.get();
      Place.on((p) => { this.place = p; this.focus = null; this.render(PAGE * 2); });
      this.render(PAGE * 2);
    }

    // A shop picked by name: its suburb becomes the place, so the shop leads the list (and the
    // map) with its neighbours after it, its row open. The field keeps the shop's name.
    showShop(s) {
      Place.set({ label: `${s.suburb} ${s.state} ${s.postcode}`, at: s.at });
      this.search.input.value = s.name;
      this.chain = '';
      this.chains.querySelectorAll('[data-value]').forEach((c) => c.setAttribute('aria-pressed', c.dataset.value === '' ? 'true' : 'false'));
      this.focus = shopKey(s);
      this.render(PAGE * 2);
      const pin = this.pins.find((p) => shopKey(p) === this.focus);
      if (pin) this.map?.select(pin, true);
    }

    pick(e, key, group) {
      const chip = e.target.closest('[data-value]');
      if (!chip) return;
      this.focus = null;
      this[key] = chip.dataset.value;
      group.querySelectorAll('[data-value]').forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
      this.render(PAGE * 2);
    }

    // The filters changed: the map shows every shop they allow and frames the place and its nearest
    // few shops, or the state's shops; the list follows.
    render(count) {
      this.pins = this.shops.filter((s) => this.chain === '' || (this.chain === '-' ? !s.chain : s.chain === this.chain));
      this.states.hidden = !!this.place;
      if (this.place) this.pins = this.pins.map((s) => ({ ...s, km: km(this.place.at, s.at) })).sort((a, b) => a.km - b.km);
      const inState = this.pins.filter((s) => s.state === this.state);
      this.fill(count);
      this.map?.show({ shops: this.pins, focus: this.place ? this.pins.slice(0, NEARBY + 2) : inState, place: this.place });
    }

    // The list: with the map on screen, the shops it shows, nearest the place (or the map's centre)
    // first; otherwise the nearest to the place, or the state's shops.
    fill(count) {
      let shops;
      let distances = true;
      if (this.map?.drives()) {
        // Near the place while it's on the map; once the visitor moves away, what's on the map,
        // nearest its centre first, without distances from a place they've left.
        const near = this.place && this.map.contains(this.place.at);
        const from = near ? this.place.at : this.map.centre();
        shops = this.map.visible(this.pins).sort((a, b) => km(from, a.at) - km(from, b.at));
        distances = near;
        if (!shops.length) this.status.textContent = this.labels.emptyMap;
        else if (near) this.status.textContent = this.labels.near.replace('[place]', this.place.label);
        else this.status.textContent = shops.length === 1 ? this.labels.onMapOne : this.labels.onMap.replace('[count]', shops.length);
      } else if (this.place) {
        shops = this.pins;
        this.status.textContent = this.labels.near.replace('[place]', this.place.label);
      } else {
        shops = this.pins.filter((s) => s.state === this.state)
          .sort((a, b) => a.suburb.localeCompare(b.suburb) || a.name.localeCompare(b.name));
        this.status.textContent = this.labels.count.replace('[count]', shops.length).replace('[state]', this.state);
      }
      if (count <= PAGE * 2 && this.results) this.results.scrollTop = 0;
      this.shown = Math.min(count, shops.length);
      const list = shopList(shops.slice(0, this.shown), {
        labels: this.labels, iconStroke: this.iconStroke, address: true, brands: this.brands, distances,
        onOpen: (s) => { this.focus = shopKey(s); this.map?.select(s); },
        onClose: () => { this.focus = null; },
      });
      // The shop picked or opened stays open as the list follows the map.
      const at = this.focus ? shops.slice(0, this.shown).findIndex((s) => shopKey(s) === this.focus) : -1;
      if (at >= 0) {
        const item = list.children[at];
        item.querySelector('.stockist-nearby__row').setAttribute('aria-expanded', 'true');
        item.querySelector('.stockist-nearby__actions').hidden = false;
      }
      this.list.replaceChildren(list);
      this.more.hidden = this.shown >= shops.length;
    }
  }

  const finders = new Map();
  const finderFor = (drawer) => {
    if (!finders.has(drawer)) finders.set(drawer, new Finder(drawer));
    return finders.get(drawer);
  };

  const init = () => {
    // Shop pages: the Call button becomes the plain number where the device can't call.
    if (!canCall()) {
      document.querySelectorAll('a[data-stockist-phone]').forEach((a) => {
        const number = el('span', `${a.className} stockist-page__phone`, a.dataset.stockistPhone);
        a.replaceWith(number);
      });
    }
    document.querySelectorAll('[data-stockist-location]').forEach((root) => new LocationBar(root));
    document.querySelectorAll('[data-stockist-directory]').forEach((root) => new Directory(root));
    document.querySelectorAll('drawer-content.stockist-shops').forEach((drawer) => {
      const finder = finderFor(drawer);
      // Product pages: a panel under the template's Find a stockist button.
      document.querySelectorAll('.product__info a[href$="/pages/stockists"], .product-meta a[href$="/pages/stockists"]')
        .forEach((link) => {
          if (link.nextElementSibling?.matches('.stockist-nearby')) return;
          const panel = el('div', 'stockist-nearby');
          link.insertAdjacentElement('afterend', panel);
          finder.addPanel(panel);
        });
      // Range pages: the bar, and its cards.
      document.querySelectorAll(`[data-stockist-bar="${drawer.id}"]`).forEach((bar) => finder.addBar(bar));
      document.querySelectorAll(`[data-stockist-nearby="${drawer.id}"]`).forEach((panel) => finder.addPanel(panel));
    });
    // A template's own spot for the nearest shops: `<div data-stockist-nearby></div>` anywhere on a
    // page with one finder.
    const first = document.querySelector('drawer-content.stockist-shops');
    if (first) {
      document.querySelectorAll('[data-stockist-nearby=""]').forEach((panel) => {
        panel.classList.add('stockist-nearby');
        finderFor(first).addPanel(panel);
      });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-stockist-open]');
    const link = !opener && e.target.closest('a[href$="/pages/stockists"]');
    if (!opener && !(link && link.closest('.product, product-meta, .product-meta, product-form, .product__info'))) return;
    const drawer = opener ? document.getElementById(opener.dataset.stockistOpen) : document.querySelector('drawer-content.stockist-shops');
    if (!drawer) return;
    e.preventDefault();
    finderFor(drawer).open();
  });

  window.StockistFinder = { finderFor, Place };
})();
